import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

// Error Boundary to catch and suppress ResizeObserver errors
class ResizeObserverErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State | null {
    // Check if it's a ResizeObserver error
    const isResizeObserverError = 
      error.message?.toLowerCase().includes('resizeobserver') ||
      error.message?.toLowerCase().includes('resize observer') ||
      error.message?.toLowerCase().includes('loop completed') ||
      error.message?.toLowerCase().includes('undelivered notifications') ||
      error.stack?.toLowerCase().includes('resizeobserver');

    if (isResizeObserverError) {
      // Suppress ResizeObserver errors - they're benign
      return null; // Don't update state, let the app continue
    }

    // For other errors, set hasError to true
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Check if it's a ResizeObserver error
    const isResizeObserverError = 
      error.message?.toLowerCase().includes('resizeobserver') ||
      error.message?.toLowerCase().includes('resize observer') ||
      error.message?.toLowerCase().includes('loop completed') ||
      error.message?.toLowerCase().includes('undelivered notifications') ||
      error.stack?.toLowerCase().includes('resizeobserver');

    if (isResizeObserverError) {
      // Suppress ResizeObserver errors - they're benign
      console.debug('ResizeObserver error suppressed by Error Boundary:', error.message);
      return; // Don't log or handle ResizeObserver errors
    }

    // Log other errors normally
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Only show error UI for non-ResizeObserver errors
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Something went wrong.</h2>
          <p>Please refresh the page.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ResizeObserverErrorBoundary;

